"use client";

import React, { useEffect, useMemo, useRef } from "react";
import { StatusScreen } from "@mercadopago/sdk-react";
import { TarotApiService } from "@/services/tarotApi";
import { useI18n } from "@/i18n/I18nProvider";

type Props = {
  requestId: number;
  paymentId: string;
  onSuccess?: (status: "approved" | "authorized") => void;
};

export default function MercadoPagoStatusScreen({
  requestId,
  paymentId,
  onSuccess,
}: Props) {
  const { t } = useI18n();
  const successTimerRef = useRef<number | null>(null);
  const notifiedRef = useRef(false);
  const skipStatusBrick = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isInAppBrowser = /Instagram|FBAN|FBAV|FB_IAB/i.test(ua);
    return isIOS && isInAppBrowser;
  }, []);
  const containerId = useMemo(
    () => `statusScreenBrick_container_${requestId}_${paymentId}`,
    [requestId, paymentId],
  );

  useEffect(() => {
    // Polling com backoff: sem ele, uma sessão expirada gerava uma falha (e um
    // registro de erro no backend) a cada 3s, indefinidamente — era a maior
    // fonte de ruído do error_logs. Em falha o intervalo dobra até 30s; em
    // sucesso volta ao ritmo normal; após confirmar o pagamento, para.
    const POLL_MS = 3000;
    const MAX_BACKOFF_MS = 30000;
    let cancelled = false;
    let pollTimer: number | null = null;
    let consecutiveFailures = 0;

    const schedule = (delay: number) => {
      if (cancelled) return;
      pollTimer = window.setTimeout(fetchStatus, delay);
    };

    const fetchStatus = async () => {
      if (cancelled) return;
      try {
        const resp = await TarotApiService.getPaymentStatusForRequest(
          requestId
        );
        consecutiveFailures = 0;
        const s = String(resp?.status || "").toLowerCase();
        if (
          (s === "approved" || s === "authorized") &&
          !notifiedRef.current &&
          successTimerRef.current === null
        ) {
          // Keep the status brick mounted briefly before leaving this screen.
          // This avoids a race where MP Brick async create runs after unmount.
          successTimerRef.current = window.setTimeout(() => {
            notifiedRef.current = true;
            onSuccess?.(s as "approved" | "authorized");
          }, 1200);
          return; // pagamento confirmado; não há mais o que consultar
        }
        schedule(POLL_MS);
      } catch {
        consecutiveFailures++;
        schedule(
          Math.min(POLL_MS * 2 ** consecutiveFailures, MAX_BACKOFF_MS)
        );
      }
    };

    fetchStatus();
    return () => {
      cancelled = true;
      if (pollTimer !== null) {
        window.clearTimeout(pollTimer);
      }
      if (successTimerRef.current !== null) {
        window.clearTimeout(successTimerRef.current);
        successTimerRef.current = null;
      }
    };
  }, [requestId, onSuccess]);

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-gray-200 overflow-hidden">
        {skipStatusBrick ? (
          <div className="p-4 text-sm text-gray-600">
            {t(
              "payment.status.pending",
              "Pagamento em processamento. Aguarde a confirmação.",
            )}
          </div>
        ) : (
          <StatusScreen id={containerId} initialization={{ paymentId }} />
        )}
      </div>

      <p className="text-xs text-gray-500">
        Dica: se pagou via Pix, a compensação costuma acontecer em segundos.
        Cartão de crédito pode exigir uma análise rápida.
      </p>
    </div>
  );
}
